import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { getAverageRGB, getRemotedbUrl, i18nIntl, isEmpty, isNotEmpty, removeAccents } from '../lib/ep.js';
import { useCSS } from '../lib/hooks.js';
import { stage_promotion_types as spt } from '../data/bootstrap_stages.js';

import ActionButton, { CreateButton, SecondaryButton, SquaredButton } from '../components/builder/ActionButton.js';
import ActionLink from '../components/builder/ActionLink.js';
import Autocomplete from '../components/builder/Autocomplete.js';
import { BuilderMainWithoutSidebar } from '../components/builder/BuilderMain.js';
import ButtonCollection from '../components/builder/ButtonCollection';
import ColorPicker from '../components/builder/ColorPicker.js';
import { DeleteButton } from '../components/builder/ConfirmButton.js';
import FloatingLayer from '../components/builder/FloatingLayer.js';
import { AutoPostImageUploader } from '../components/builder/ImageUploader.js';
import Input from '../components/builder/Input.js';
import Model from '../components/builder/Model.js';
import { NavigationTabsBuilder } from '../components/builder/NavigationTabs';
import SaveButton from '../components/builder/SaveButton.js';
import SearchField from '../components/builder/SearchField.js';
import ToggleButton from '../components/builder/ToggleButton.js';
import Tooltip from '../components/builder/Tooltip.js';

import ModuleHeader from '../components/ui_items/ModuleHeader.js';

import { getFullImage, getTeamName } from '../components/front/GuessScoreClient.js';

import Select from '../components/builder/Select.js';
import SectionTitle from '../components/builder/SectionTitle.js';
import RibbonCollection from '../components/ui_items/RibbonCollection.js';
import FieldLabel from '../components/builder/FieldLabel.js';
import { TranslationField } from '../components/builder/DynamicFields.js';
import { useAccount } from '../components/context/accountContext.js';

const default_team = {
	id:null,
	name:'',
	short_name:'',
	external_id:null,
	image:'',
};

export const SingleTeam = ({ team, ...props }) => {
	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ selected, setSelected ] = useState(props?.selected ?? false);

	const can_toggle = (props.toggleTeam != undefined) ? true : false;
	const can_drag = props?.can_drag ?? false;
	const image_size = props?.image_size ?? 'big';
	const already_in_catalogue = props?.already_in_catalogue ?? false;
	const team_sport = props?.sport ?? false;

	const categories = props?.categories ?? [];

	const can_remove = (props.onRemove != undefined) ? true : false;
	const remove_disabled = props?.remove_disabled ?? false;
	const can_edit = (props.onEdit != undefined) ? true : false;
	const can_translate = (props?.onTranslate != undefined) ? true : false;
	const display_categories = props?.display_categories ?? false;
	const display_empty_categories = props?.display_empty_categories ?? false;
	const is_other = props?.is_other ?? false;

	const has_actions = (can_remove || can_edit || can_translate) ? true : false;

	const toggleStatus = () => {
		if(can_toggle === false) return;
		if(already_in_catalogue === true) return;
		setSelected(!selected);
		if(props.toggleTeam != undefined) props.toggleTeam(team);
	};

	const onClick = (e) => {
		if(props.onClick == undefined) return;
		if(already_in_catalogue === true) return;
		e.stopPropagation();
		props.onClick(team);
	};

	const startDrag = (e) => {
		if(can_drag === false) return;
		e.dataTransfer.setData('text/plain', team.id);
	};

	useEffect(() => {
		setSelected(props?.selected ?? false);
	}, [ props.selected ]);

	useEffect(() => {
		if(can_drag === false) return;

		// NATIVE JS DRAG'N'DROP DOCUMENTATION:
		// https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations

		const draggableElement = document.querySelector(`#team_${team.id}`);

		draggableElement.addEventListener('dragstart', (e) => startDrag(e));

		return () => {
			draggableElement.removeEventListener('dragstart', startDrag);
		}
	}, []);

	const classes = ['single_team'];
	if(props.classes) classes.push(props.classes);
	if(selected) classes.push('selected');
	if(can_drag) classes.push('draggable');
	if(can_toggle) classes.push('toggleable');
	if(already_in_catalogue === true) classes.push('selected already_in');

	const image = getFullImage(team, image_size);

	let team_categories = [];
	if(categories.length > 0 && isNotEmpty(team, 'categories') && team.categories.length > 0) {
		team.categories.map((c) => {
			const _c = categories.find((i) => (i.id == c));
			if(_c == undefined) return;
			team_categories.push(_c);
		});
	}
	if(team_categories.length == 0 && display_empty_categories) {
		team_categories.push({
			title:"No category",
			color:'lightgray'
		});
	}

	return (
		<div
			id={(can_drag) ? `team_${team.id}` : null}
			class={classes.join(" ")}
			onClick={toggleStatus}
			draggable={can_drag}
		>
			{(can_drag) && (
				<ButtonCollection>
					<SquaredButton
						icon='knk-move'
					/>
				</ButtonCollection>
			)}

			{(team_sport !== false && props.onClick == undefined) && (
				<SquaredButton
					icon={team_sport.icon}
					tooltip={<Tooltip
						with_icon={false}
						autotranslate={false}
						position='top'
					>{team_sport.v}</Tooltip>}
					_class={{ other:'sport_icon not_bordered_button' }}
				/>
			)}

			{(image !== false) ? (
				<div
					class="team_img"
				>
					<img
						src={image}
						alt={`${team.name} logo`}
					/>
				</div>
			) : (
				<div
					class="team_img"
					style={{
						backgroundColor:team?.color ?? `rgba(30, 34, 170, 1)`,
						borderRadius:'10em',
					}}
				/>
			)}

			{(is_other === false) && (
				<strong
					class="team_name"
				>{getTeamName(team)}</strong>
			)}

			{(display_categories && categories.length > 0) && (
				<RibbonCollection
					items={team_categories}
				/>
			)}

			{(has_actions && is_other === false) && (
				<ButtonCollection
					_class={{ other:'gui_not_in_top_tiny' }}
				>
					{(can_translate) && (
						<SquaredButton
							icon='knk-translate'
							onClick={() => props.onTranslate(team.id)}
						/>
					)}

					{(can_edit) && (
						<SquaredButton
							icon='knk-edit'
							onClick={() => props.onEdit(team.id)}
						/>
					)}

					{(can_remove) && (
						<DeleteButton
							squared={true}
							disabled={(remove_disabled) ? true : false}
							tooltip={(remove_disabled) ? <Tooltip
								with_icon={false}
								position='top'
							>This team is in a match, so it can't be deleted.</Tooltip> : null}
							onClick={() => props.onRemove(team.id)}
							_position='top'
						/>
					)}
				</ButtonCollection>
			)}

			{(props.onClick != undefined) && (
				<ButtonCollection>
					{(team_sport !== false) && (
						<SquaredButton
							icon={team_sport.icon}
							tooltip={<Tooltip
								with_icon={false}
								autotranslate={false}
								position='top'
							>{team_sport.v}</Tooltip>}
							_class={{ other:'sport_icon not_floating not_bordered_button' }}
						/>
					)}

					<SquaredButton
						icon='knk-arrow-r-big'
						onClick={onClick}
					/>
				</ButtonCollection>
			)}

			{(can_toggle && is_other === false) && (
				<ButtonCollection
					_class={{ other:'toggle_check' }}
				>
					{(already_in_catalogue === false) ? (
						<ToggleButton
							active={selected}
							color='mainText'
							icon='knk-checkbox-on'
							icon_off='knk-checkbox-off'
						/>
					) : (
						<span
							style={{
								position:'relative',
								top:'-0.5em',
							}}
						>[ {i18n("Already added")} ]</span>
					)}
				</ButtonCollection>
			)}

			{(is_other) && (
				<ButtonCollection
					_class={{ other:'other_remove' }}
				>
					<SquaredButton
						icon='ep-delete'
					/>
				</ButtonCollection>
			)}
		</div>
	)
};

export const TeamsList = ({ teams, ...props }) => {
	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const default_filter = {
		search:"",
		category:'',
	};

	const [ filter, setFilter ] = useState(default_filter);

	const changeFilter = (k, v) => {
		setFilter({ ...filter, [k]:v.trim() });
	};

	const used_teams = props?.used_teams ?? [];
	const teams_already_in_catalogue = props?.teams_already_in_catalogue ?? [];
	const layout = props?.layout ?? 'grid';
	const hide_used = props?.hide_used ?? false;
	const categories = props?.categories ?? [];
	const is_other = props?.is_other ?? false;

	const display_filter = props?.display_filter ?? false;
	const filter_enabled = (teams.length == 0) ? false : true;

	const classes = ['teams_list', `${layout}_layout`];
	if(props.classes) classes.push(props.classes);

	const filtered_teams = teams.filter((team) => {
		if(hide_used && used_teams.includes(team.id)) return false;
		if(filter.search == "" && filter.category == '') return true;

		let passes_search = true;
		if(filter.search != "") {
			passes_search = (removeAccents(`${team.short_name}_${team.name}`).indexOf(removeAccents(filter.search)) > -1) ? true : false;
		}

		let passes_category = true;
		if(categories.length > 0 && filter.category != '') {
			passes_category = (isNotEmpty(team, 'categories') && team.categories.length > 0 && team.categories.includes(filter.category)) ? true : false;
		}

		return (passes_search && passes_category) ? true : false;
	}).sort((a, b) => {
		return (getTeamName(a) < getTeamName(b)) ? -1 : 1;
	});

	const getEmptyListMessage = () => {
		if(layout == 'list' && teams.length == 0) return "There are no teams yet.";
		if(filter.search == "") return "Search to find teams to add.";
		return "There are no results.";
	};

	const category_options = [{ k:'', v:i18n("All categories") }];
	categories.map((c) => {
		category_options.push({ k:c.id, v:c.title });
	});

	const sports = getSportsList(props.intl);

	return (
		<div class="teams_list_c">
			{(display_filter === false && isNotEmpty(props, 'children')) && (
				<FieldLabel>{props.children}</FieldLabel>
			)}

			{(layout == 'list' && filter_enabled === false && isNotEmpty(props, 'children')) && (
				<div class="list_header with_categories">
					{props.children}
				</div>
			)}

			{(display_filter && filter_enabled) && (
				<div class={`list_header${(categories.length > 0) ? ' with_categories' : ''}`}>
					<Model
						model={filter}
						change={changeFilter}
						classname="filter"
					>
						<SearchField
							name='search'
							_class={{ first:true }}
						/>

						{(categories.length > 0) && (
							<Select
								name='category'
								values={category_options}
								_class={{ first:true }}
							/>
						)}
					</Model>

					{(props.children != undefined) && (
						props.children
					)}
				</div>
			)}

			<ul class={classes.join(" ")}>
				{filtered_teams.length > 0 && filtered_teams.map((team) => {
					const is_used = used_teams.includes(team.id);
					const already_in_catalogue = teams_already_in_catalogue.find((t) => team.id == t.external_id);

					return (
						<li key={`team_${team.id}`}>
							<SingleTeam
								team={team}
								selected={team?.selected ?? false}
								remove_disabled={(is_used) ? true : false}
								already_in_catalogue={(already_in_catalogue != undefined) ? true : false}
								sport={(is_other === false) ? sports.find((s) => {
									if(team.sport_id == undefined) return false;
									return (s.k == team.sport_id) ? true : false;
								}) : null}
								{...props}
							/>
						</li>
					)
				})}

				{(filtered_teams.length == 0 && is_other === false) && (
					<li class={(layout == 'grid') ? 'empty_filter' : null}>
						<p>{i18n(getEmptyListMessage())}</p>
					</li>
				)}

				{(is_other) && (
					[...Array(20).keys()].map((i) => {
						return (
							<li key={`placeholder_${i}`} class='team_placeholder' />
						)
					})
				)}
			</ul>
		</div>
	)
};

const getSportsList = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const sports = [
		{ k:'', v:`-- ${i18n("All sports")} --` },
		{ k:102, v:i18n("Football / Soccer"), icon:'knk-football-ball' },
		{ k:105, v:i18n("Baseball"), icon:'knk-baseball-ball' },
		{ k:106, v:i18n("Basketball"), icon:'knk-basketball-ball' },
		{ k:107, v:i18n("American Football"), icon:'knk-american-football-ball' },
		{ k:108, v:i18n("Ice Hockey"), icon:'knk-ice-skate' },
		{ k:110, v:i18n("Rugby"), icon:'knk-rugby-ball' },
		{ k:112, v:i18n("Cricket"), icon:'knk-cricket' },
		{ k:115, v:i18n("E-Sports"), icon:'knk-videogame' },
		{ k:118, v:i18n("Handball"), icon:'knk-handball-ball' },
		{ k:137, v:i18n("Lacrosse"), icon:'knk-lacrosse' },
	];

	//sports.sort((a, b) => a.v.localeCompare(b.v));
	return sports;
};

export const TeamsSelector = (props) => {
	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const { MyAccount } = useAccount();
	const language = MyAccount.getAgentLang();

	const selected_teams = props?.selected_teams ?? [];

	const add_type = props?.add_type ?? 'button';	

	const sports = getSportsList(props.intl);

	const [ teams_category, setTeamsCategory ] = useState({ categories:[] });

	const tmp_filtered_teams = useRef([]);

	const [ state, dispatch ] = useReducer((state, action) => {
		switch(action.type) {
			case 'CHANGE':
				let _state = {...state};
				_state[action.k] = action.v;
				if(action.k == 'filtered_teams' && action.v != [])tmp_filtered_teams.current = action.v;
				return {
					..._state
				};

			case 'FILTER_SPORT':
				return {
					...state,
					filtered_teams:tmp_filtered_teams.current.filter((t) => (t.sport_id == action.v || isEmpty(action,'v',['']))),
					[action.k]:action.v,
				};

			case 'TOOGLE_SELECTED_TEAM':
				const actual_team = action?.team ?? {};
				const already = state.selected_teams.find((team) => (team.id == actual_team.id));

				let selected_teams;
				if(already) {
					selected_teams = state.selected_teams.filter((team) => (team.id !== actual_team.id));
				} else {
					selected_teams = [ ...state.selected_teams, actual_team ];
				}
				
				return {
					...state,
					selected_teams,
				};

			case 'ALL_SELECTED_TEAM':
				return {
					...state,
					selected_teams:state.filtered_teams,
				};

			case 'SET_COMPETITION':
				tmp_filtered_teams.current = action.competition.teams;
				return {
					...state,
					filtered_teams:action.competition.teams,
					sport_id:action.competition.sport_id,
					competition:action.competition.name,
				};

			case 'OPEN_LAYER':
				return {
					...state,
					display_add_team:true,
					filtered_teams:[],
					selected_teams:[],
					search:'',
					competition:'',
					sport_id:null,
				};

			case 'CLOSE_LAYER':
				return {
					...state,
					display_add_team:false,
				};

			case 'ADD_TEAM':
				return {
					...state,
					display_add_team:true,
					team:default_team,
				};
		}
	}, {
		display_add_team:false,
		filtered_teams:[],
		selected_teams:[],
		sport_id:null,
		search:'',
		competition:'',
	});

	const setToPromotion = (teams, cb) => {
		const _teams = [ ...selected_teams ];
		const already_in_collection = _teams.map((team) => team.external_id);

		teams.forEach((t) => {
			if(already_in_collection.includes(t.external_id)) return;

			t.id = uuidv4();
			t.type = 'team';
			t.image = null;
			t.orig_name = t.name;
			t.orig_short_name = t.short_name;
			if(teams_category.categories.length > 0) t.categories = teams_category.categories;
			delete t.image_url;
			delete t.thumbnail_url;
			_teams.push(t);
		});

		props.promoChange('guess_score_teams', _teams);
		if(cb != undefined) cb(_teams);
	};

	const addTeam = (team, cb) => {
		const _teams = [ ...selected_teams ];
		_teams.push(team);
		props.promoChange('guess_score_teams', _teams);
		props.addTeams(_teams);
		if(cb != undefined) cb();
	};

	const debounceTimeoutTeam = useRef(null);
	
	const useDebouncedSearchTeam = (delay = 500) => {
		const searchTeam = (k, v) => {
			if(!v || v.length < 3) return;

			if(debounceTimeoutTeam.current) {
				clearTimeout(debounceTimeoutTeam.current);
			}

			debounceTimeoutTeam.current = setTimeout(() => {
				let url_team = `/v24/team/search/${v}`;
				if(state.sport_id) url_team+=`/${state.sport_id}`
				$.get(getRemotedbUrl(url_team), (r) => {
					if(r.status == '0') {
						dispatch({ type:'CHANGE', k:'filtered_teams', v:[] });
						return;
					}
					dispatch({ type:'CHANGE', k:'filtered_teams', v:r.data });
				}, 'JSON');

				dispatch({ type:'CHANGE', k, v });
			}, delay);
		};

		return searchTeam;
	};

	const debounceTimeoutCompetition = useRef(null);

	const useDebouncedSearchCompetition = (delay = 500) => {
		const searchCompetition = (value, cb) => {
			if(!value || !value.value || (value.value).length < 3) {
				cb();
				return;
			}
			dispatch({ type:'CHANGE', k:'sport_id', v:'' });
			if(debounceTimeoutCompetition.current) {
				clearTimeout(debounceTimeoutCompetition.current);
			}

			debounceTimeoutCompetition.current = setTimeout(() => {
				let url_competition = `/v24/competition/search/${value.value}/${language}`;
				if(state.sport_id) url_competition += `/${state.sport_id}`
				$.get(getRemotedbUrl(url_competition), (r) => {
					if(r.status == '1') {
						const suggestions = r.data.map((item) => ({
							value:<div>{`${item.name} [${item.teams.length}]`}</div>,
							data:item,
						}));
						dispatch({ type:'CHANGE', k:'search', v:'' });
						cb(suggestions);
					} else {
						cb();
					}
				}, 'JSON');
			}, delay);
		};
		
		return searchCompetition;
	};

	const selectCategory = (k, v) => {
		setTeamsCategory({ ...teams_category, [k]:[v] });
	};

	const AddComponent = (add_type == 'link') ? ActionLink : ActionButton;
	const has_created_teams = ((props?.selected_teams ?? []).length > 0) ? true : false;

	const selected_teams_not_in_my_collection = state.selected_teams.filter((t) => {
		if(t.external_id == undefined) return true;

		const already_in_my_collection = selected_teams.find((t_in) => {
			if(t_in.external_id == undefined) return false;
			if(t_in.external_id == t.external_id) return true;
			return false;
		});

		if(already_in_my_collection != undefined) return false;
		return true;
	});

	return (
		<>
			{(props.children_if_empty != undefined && !has_created_teams) ? (
				<div
					onClick={() => dispatch({ type:'OPEN_LAYER' })}
				>{props.children_if_empty}</div>
			) : (
				<>
					{(props.children != undefined) && (
						<div
							onClick={() => dispatch({ type:'OPEN_LAYER' })}
						>{props.children}</div>
					)}

					{(props.children == undefined) && (
						<AddComponent
							icon='knk-add-item'
							onClick={() => dispatch({ type:'OPEN_LAYER' })}
						>Add teams</AddComponent>
					)}
				</>
			)}

			{(state.display_add_team) && (
				<FloatingLayer
					backButton={() => dispatch({ type:'CLOSE_LAYER' })}
					_class={{ size:'big' }}
				>
					<div>
						<ModuleHeader>Add teams</ModuleHeader>

						<div
							class="gui_flexbox gui_flex_align_center gui_not_in_top"
							style={{
								gap:'0.5em',
								width:'100%',
							}}
						>	
							<Autocomplete
								suggestId='competition'
								fetch={useDebouncedSearchCompetition(500)}
								value={state.competition}
								autoclear={true}
								_class={{ first:true, other:'search' }}
								autotranslate_placeholder={true}
								placeholder="Search by competition"
								selected={(c) => {
									dispatch({ type:'SET_COMPETITION', competition:c.suggestion.data });
								}}
								_field_style={{
									'--autosuggest-option-w':'40em'
								}}
							/>

							<SearchField
								name='search'
								model={state}
								change={useDebouncedSearchTeam(500)}
								placeholder="Search by team name"
								_class={{ first:true }}
							/>

							<Select
								name='sport_id'
								values={sports}
								_class={{ first:true }}
								value={state?.sport_id ?? null}
								placeholder="Select an sport"
								change={(k,v)=>dispatch({ type:'FILTER_SPORT', k, v})}
							/>

							<ButtonCollection>
								<ActionButton
									onClick={() => dispatch({ type:'ALL_SELECTED_TEAM' })}
									disabled={(state.selected_teams.length < state.filtered_teams.length) ? false : true}
								>Select all</ActionButton>
							
								<ActionButton
									onClick={() => dispatch({ type:'CHANGE', k:'selected_teams', v:[] })}
									disabled={(state.selected_teams.length > 0) ? false : true}
								>Deselect all</ActionButton>

								<SingleTeamEdit
									teams={props.teams}
									categories={props.categories}
									promotion_id={props.promotion_id}
									promoChange={props.promoChange}
									addCategory={props.addCategory}
									onSave={addTeam}
									type='create'
									intl={props.intl}
								/>
							</ButtonCollection>
						</div>

						<div class="gui_not_in_top">
							<TeamsList
								teams={state.filtered_teams.map((team) => ({
									...team,
									image:getFullImage({ ...team, external_id:team.id }), //Es sempre from external, static path
									selected:(state?.selected_teams ?? []).find((t) => (t.id == team.id)) ? true : false,
								}))}
								classes='in_layer'
								teams_already_in_catalogue={selected_teams}
								categories={props.categories}
								image_size='small'
								promotion_id={promotion.id}
								toggleTeam={(team) => dispatch({ type:'TOOGLE_SELECTED_TEAM', team })}
								intl={props.intl}
							/>

							<TeamsList
								teams={selected_teams_not_in_my_collection}
								is_other={true}
								classes='in_layer in_other_selected'
								image_size='small'
								promotion_id={promotion.id}
								toggleTeam={(team) => dispatch({ type:'TOOGLE_SELECTED_TEAM', team })}
								intl={props.intl}
							>Selected teams</TeamsList>
						</div>

						<TeamCategorySelector
							categories={props.categories}
							addCategory={props.addCategory}
							model={teams_category}
							change={selectCategory}
							disabled={((state?.selected_teams ?? []).length > 0) ? false : true}
							intl={props.intl}
						/>

						<ButtonCollection
							in_floating={true}
						>
							<SaveButton
								type='confirm'
								confirmMessage={`<strong>${i18n("Notice on the use of trademarked content")}</strong><br /><p>${i18n("Team names, badges and logos may be protected by intellectual property rights. Easypromos provides this tool to help users find relevant information, but it is the organizer’s responsibility to ensure they have the necessary rights or permissions to use this content in their promotion.")}</p>`}
								autotranslate_confirmMessage={false}
								_position='top'
								autotranslate={false}
								disabled={(state.selected_teams.length < 1) ? true : false}
								onClick={(e, cb) => setToPromotion(state.selected_teams, (teams) => {
									dispatch({ type:'OPEN_LAYER' });
									if(props.addTeams) props.addTeams(teams);
									cb();
								})}
							>{`${props.intl.formatMessage({ id:"Add {num} teams" }, { num:selected_teams_not_in_my_collection.length })}`}</SaveButton>

							<SecondaryButton
								onClick={() => dispatch({ type:'CLOSE_LAYER' })}
							>Close</SecondaryButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</>
	)
};

const SingleTeamEdit = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const type = props?.type ?? 'create';
	// const control_save = useRef(false);

	const [ state, dispatch ] = useReducer((state, action) => {
		switch(action.type) {
			case 'OPEN_LAYER':
				if(state.team.id == undefined) {
					return {
						...state,
						team:{ ...default_team, id:uuidv4() },
						display:true,
					};
				}
				return {
					...state,
					team:default_team,
					display:true,
				};

			case 'CLOSE_LAYER':
				if(props.onClose != undefined) props.onClose();
				return {
					...state,
					team:default_team,
					display:false,
				};

			case 'EDIT_TEAM':
				return {
					...state,
					team:{ ...state.team, [action.k]:action.v },
				};

			case 'SAVE_TEAM':
				//ADD
				// if(type == 'create') {
				// 	if(!control_save.current && props.onSave != undefined){
				// 		control_save.current = true;//evitem doble save que no hem sabut controlar
				// 		props.onSave(state.team);
				// 	}
				// 	return {
				// 		...state,
				// 		team:default_team,
				// 		display:false,
				// 	};
				// }
				//EDIT
				const _teams = [ ...props.teams ];
				_teams.map((_team, t) => {
					if(_team.id != state.team.id) return;
					_teams[t] = state.team;
				});
				
				props.promoChange('guess_score_teams', _teams);
				if(props.onSave != undefined) props.onSave(_teams);

				return {
					...state,
					team:default_team,
					display:false,
				};
		}
	}, {
		team:props?.team ?? default_team,
		display:props?.force_display ?? false,
	});

	const getImageColor = (team) => {
		if(isNotEmpty(team, 'color')) {
			return team.color;
		}

		const img = getFullImage(team);

		const i = new window.Image();
		i.crossOrigin = 'Anonymous';
		if(img)i.src = img;
		i.onload = () => {
			const avg_color = getAverageRGB(i);
			const color = `rgba(${avg_color.r}, ${avg_color.g}, ${avg_color.b}, 1)`;
			dispatch({ type:'EDIT_TEAM', k:'color', v:color });
			dispatch({ type:'EDIT_TEAM', k:'orig_color', v:color });
		};
	};

	const changeTeam = (k, v) => {
		if(k == 'full_image' && v != null && v != '') {
			const i = new window.Image();
			i.crossOrigin = 'Anonymous';
			i.src = v;
			i.onload = () => {
				const avg_color = getAverageRGB(i);
				dispatch({ type:'EDIT_TEAM', k:'color', v:`rgba(${avg_color.r}, ${avg_color.g}, ${avg_color.b}, 1)` });
			};
		}

		if(k == 'categories') {
			dispatch({ type:'EDIT_TEAM', k, v:(v != '0') ? [v] : null });
			return;
		}
		dispatch({ type:'EDIT_TEAM', k, v });
	};

	const saveTeam = (e, cb) => {
		dispatch({ type:'SAVE_TEAM' });
		if(cb != undefined) cb();
	};

	const createTeam = (e, cb) => {
		props.onSave(state.team);
		dispatch({ type:'CLOSE_LAYER' });
	}

	const enable_save = (state?.team?.name ?? '').length > 1 ? true :false;

	return (
		<>
			{(type == 'create') && (
				<CreateButton
					onClick={() => dispatch({ type:'OPEN_LAYER' })}
				>Create new team</CreateButton>
			)}

			{(state.display) && (
				<FloatingLayer
					backButton={() => dispatch({ type:'CLOSE_LAYER' })}
					_class={{ size:'small' }}
				>
					<div>
						<ModuleHeader>
							{(type == 'create') ? "Create team" : "Edit team"}
						</ModuleHeader>

						<div>
							<Model
								model={state.team}
								change={changeTeam}
							>
								<Input
									name='name'
									max_length={30}
									_class={{ size:50 }}
									placeholder={getTeamName(state.team, 'name')}
									autotranslate_placeholder={false}
								>Team name</Input>

								<Input
									name='short_name'
									max_length={3}
									_class={{ size:50 }}
									placeholder={getTeamName(state.team, 'short_name')}
									autotranslate_placeholder={false}
								>Team short name</Input>

								<AutoPostImageUploader
									auto_url={`/promotions/auto_img/${props.promotion_id}`}
									auto_type='teams'
									image={state.team.full_image}
									name="image"
									small_input={true}
									_size={{ width:400, height:400 }}
									_class={{ size:50 }}
								>Image</AutoPostImageUploader>

								<ColorPicker
									name="color"
									color={getImageColor(state.team)}
									enable_reset={(state.team.orig_color != state.team.color) ? true : false}
									_hide_code={true}
									_class={{ size:50 }}
								>Color</ColorPicker>

								{(type == 'create' || state.team.external_id == undefined) && (
									<Select
										name='sport_id'
										values={getSportsList(props.intl)}
										_class={{ size:50 }}
									>Sport</Select>
								)}
							</Model>

							<TeamCategorySelector
								categories={props.categories}
								addCategory={props.addCategory}
								model={state.team}
								change={changeTeam}
								intl={props.intl}
							>Team category</TeamCategorySelector>
						</div>

						<ButtonCollection
							in_floating={true}
						>
							<SaveButton
								onClick={type == 'create' ? createTeam : saveTeam}
								autotranslate={false}
								disabled={!enable_save}
							>{type == 'create' ? i18n("Create") : i18n("Save changes")}</SaveButton>

							<SecondaryButton
								onClick={() => dispatch({ type:'CLOSE_LAYER' })}
							>Close</SecondaryButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</>
	)
};

const TeamCategorySelector = ({ categories, model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const single_team = props?.single_team ?? true;
	const disabled = props?.disabled ?? false;

	const all_categories = [
		{ k:'0', v:`-- ${i18n("None")} --` },
	];
	categories.map((category) => {
		all_categories.push({
			k:category.id, v:category.title,
		});
	});

	const default_team = {
		id:uuidv4(),
		title:"",
	};

	const [ display_create, setDisplayCreate ] = useState(false);
	const [ values, setValues ] = useState(all_categories);
	const [ new_category, setNewCategory ] = useState({ ...default_team });
	const [ errors, setErrors ] = useState({});

	const createCategory = (e, cb) => {
		props.addCategory(new_category);
		setValues([ ...values, { k:new_category.id, v:new_category.title } ]);

		if(single_team) change('categories', new_category.id);

		if(cb != undefined) cb();
		setDisplayCreate(false);
	};

	const change_category = (k, v) => {
		setErrors({});
		const already_search = values.find((item) => 
			removeAccents(item.v) === removeAccents(v)
		);

		if(already_search) {
			setErrors({ ...errors, [k]:i18n("This category already exists") });
		} else {
			setNewCategory({ ...new_category, [k]:v });
		}
	};

	const openCreateLayer = () => {
		setNewCategory({ ...default_team });
		setDisplayCreate(true);
	};

	const has_errors = (Object.keys(errors).length > 0) ? true : false;

	return (
		<div>
			<Model
				model={model}
				change={change}
			>
				<Select
					name='categories'
					values={values}
					value={(model.categories != undefined && model?.categories.length > 0) ? model.categories[0] : ''}
					extra_contents={<ActionLink
						onClick={openCreateLayer}
					>Create</ActionLink>}
					disabled={disabled}
					_class={{ size:50 }}
				>{props.children ?? "Add the selected teams to a category"}</Select>
			</Model>

			{(display_create) && (
				<FloatingLayer
					_class={{ size:'small' }}
					backButton={() => setDisplayCreate(false)}
				>
					<div>
						<SectionTitle>Create new category</SectionTitle>

						<Model
							model={new_category}
							change={change_category}
							errors={errors}
						>
							<Input
								name='title'
								max_length={25}
								_class={{ first:true, size:60 }}
							>Category name</Input>
						</Model>

						<ButtonCollection
							in_floating={true}
						>
							<SaveButton
								disabled={has_errors}
								onClick={createCategory}
							>Create</SaveButton>

							<SecondaryButton
								onClick={() => setDisplayCreate(false)}
							>Cancel</SecondaryButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</div>
	)
};

const SingleTeamCategory = ({ category, teams_in_category, ...props }) => {
	const [ is_visible, setIsVisible ] = useState(true);

	return (
		<div class="team_category">
			<div class="team_category_header">
				<h2>{category.title} ({teams_in_category.length})</h2>

				<ToggleButton
					active={is_visible}
					squared={true}
					onClick={() => setIsVisible(!is_visible)}
					icon='knk-arrow-t-big'
					icon_off='knk-arrow-b-big'
					_class={{ other:'toggle_visibility not_bordered_button' }}
				/>
			</div>

			{(is_visible) && (
				<TeamsList
					teams={teams_in_category}
					{...props}
				/>
			)}
		</div>
	)
};

const StageTeamsManagement = (props) => {
	useCSS('comp/guess_score.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const section_main_path = props?.section_main_path ?? 'stages';
	const promotion = props.promotion;
	const stages = props.stagesStore.getState()?.stages?.stages ?? [];
	const categories = promotion?.guess_score_categories ?? [];

	const used_teams = [];
	const stages_with_matches = stages.filter((item) => (item.Stage.promotion_type_id == spt.SCORE_PREDICTOR.id));
	stages_with_matches.map((st) => {
		(st.Stage?.guess_score_matches ?? []).map((match) => {
			if(isNotEmpty(match, 'id_1') && !used_teams.includes(match.id_1)) used_teams.push(match.id_1);
			if(isNotEmpty(match, 'id_2') && !used_teams.includes(match.id_2)) used_teams.push(match.id_2);
		});
	});

	const [ state, dispatch ] = useReducer((state, action) => {
		switch(action.type) {
			case 'CHANGE':
				return {
					...state,
					[action.k]:action.v,
				};

			case 'TOGGLE_TEAM_LAYER':
				if(state.team.id == undefined) {
					return {
						...state,
						team:state.teams.find((team) => (team.id == action.id)),
					};
				}

				return {
					...state,
					team:default_team,
				};

			case 'TOGGLE_TRANSLATE_TEAM_LAYER':
				if(state.translate === false) {
					return {
						...state,
						translate:state.teams.find((team) => (team.id == action.id)),
					};
				}

				return {
					...state,
					translate:false,
				};

			case 'TRANSLATE':
				if(state.translate.loc == undefined) state.translate.loc = {};
				if(state.translate.loc[action.loc] == undefined) state.translate.loc[action.loc] = {};

				if(action.v == '' && state.translate.loc[action.loc][action.k] != undefined) {
					delete state.translate.loc[action.loc][action.k];
				} else {
					state.translate.loc[action.loc][action.k] = action.v;
				}

				return {
					...state,
				};

			case 'UPDATE_TRANSLATIONS':
				const _teams = [];
				state.teams.map((t) => {
					if(t.id != state.translate.id) {
						_teams.push(t);
						return;
					}

					_teams.push(state.translate);
				});
				props.promoChange('guess_score_teams', _teams);

				return {
					...state,
					teams:_teams,
					translate:false,
				};

			case 'UPDATE_TEAMS':
				return {
					...state,
					teams:action.teams,
				};

			case 'DELETE_TEAM':
				const filtered_teams = state.teams.filter((team) => (team.id !== action.id));
				props.promoChange('guess_score_teams', filtered_teams);
				return {
					...state,
					teams:filtered_teams,
				};

			case 'DELETE_ALL':
				const non_delete_teams = state.teams.filter((team) => used_teams.includes(team.id));
				props.promoChange('guess_score_teams', non_delete_teams);

				return {
					...state,
					teams:non_delete_teams,
				};
		}
	}, {
		teams:[],
		team:default_team,
		translate:false,
	});

	useEffect(() => {
		dispatch({ type:'UPDATE_TEAMS', teams:(promotion?.guess_score_teams ?? []) });
	}, []);

	const editTeam = (team_id) => {
		dispatch({ type:'TOGGLE_TEAM_LAYER', id:team_id });
	};
	
	const afterEdit = (_teams) => {
		dispatch({ type:'UPDATE_TEAMS', teams:_teams });
	};

	const translateTeam = (team_id) => {
		dispatch({ type:'TOGGLE_TRANSLATE_TEAM_LAYER', id:team_id });
	};

	const afterTranslate = (_teams) => {
		dispatch({ type:'UPDATE_TRANSLATIONS' });
	};

	const removeTeam = (team_id) => {
		dispatch({ type:'DELETE_TEAM', id:team_id });
	};

	const deleteAllTeams = (cb) => {
		dispatch({ type:'DELETE_ALL' });
		if(cb != undefined) cb();
	};

	const addCategory = (category) => {
		const _categories = [ ...categories ];
		_categories.push({ ...category });
		props.promoChange('guess_score_categories', _categories);
	};

	const actual_teams = (state?.teams?? []).map((team) => ({
		...team,
		image:getFullImage(team),
	}));

	const teams_default_category = (categories.length == 0) ? actual_teams : actual_teams.filter((t) => (isEmpty(t, 'categories')));

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	return (
		<Router hashType="noslash">
			<Switch>
				<Route exact={true} path={props.basePath}>
					<BuilderMainWithoutSidebar
						pm={props.promoModel}
						has_tabs={true}
						main_classes='overflowed with_tabs'
					>
						<NavigationTabsBuilder
							active={`/${section_main_path}/teams`}
							sections={props.sections}
						/>

						<ModuleHeader
							subtitle={i18n("List of teams in your promotion.")}
							autotranslate={false}
							actions={<>
								<DeleteButton
									onClick={(e, cb) => deleteAllTeams(cb)}
									disabled={(actual_teams.length == 0) ? true : false}
								>Delete all</DeleteButton>

								<TeamsSelector
									selected_teams={actual_teams}
									promoChange={props.promoChange}
									promotion_id={promotion.id}
									categories={categories}
									addCategory={addCategory}
									addTeams={afterEdit}
									intl={props.intl}
								/>
							</>}
						>{`${i18n("My teams")} (${actual_teams.length})`}</ModuleHeader>

						<div class="teams_by_category">
							{categories.map((category) => {
								const teams_in_category = actual_teams.filter((t) => ((t?.categories ?? []).includes(category.id)));
								if(teams_in_category.length == 0) return null;

								return (
									<SingleTeamCategory
										key={`teams_in_category_${category.id}`}
										category={category}
										teams_in_category={teams_in_category}
										categories={categories}
										onRemove={removeTeam}
										onEdit={editTeam}
										onTranslate={(avail_locales.length > 0) ? translateTeam : false}
										used_teams={used_teams}
										classes='not_overflowed'
										image_size='small'
										intl={props.intl}
									/>
								)
							})}

							{(teams_default_category.length > 0) && (
								<SingleTeamCategory
									category={{
										title:i18n("Other"),
									}}
									teams_in_category={teams_default_category}
									categories={categories}
									onRemove={removeTeam}
									onEdit={editTeam}
									onTranslate={(avail_locales.length > 0) ? translateTeam : false}
									used_teams={used_teams}
									classes='not_overflowed'
									image_size='small'
									intl={props.intl}
								/>
							)}
						</div>

						{(state.team.id != undefined) && (
							<SingleTeamEdit
								teams={state.teams}
								team={state.team}
								categories={categories}
								promotion_id={promotion.id}
								promoChange={props.promoChange}
								addCategory={addCategory}
								onSave={afterEdit}
								onClose={() => dispatch({ type:'CHANGE', k:'team', v:default_team })}
								force_display={true}
								type='edit'
								intl={props.intl}
							/>
						)}

						{(state.translate !== false) && (
							<FloatingLayer
								backButton={() => dispatch({ type:'CHANGE', k:'translate', v:false })}
							>
								<div>
									<TranslationField
										locales={avail_locales}
										fields={[{
											...state.translate,
											type:'team',
											name:state.translate?.name ?? "",
											short_name:state.translate?.short_name ?? "",
											loc:state.translate?.loc ?? {},
										}]}
										dispatch={dispatch}
										field_types={[{
											type:'team',
											translate:[{
												key:'name',
												type:'input',
												label:"Team name",
											}, {
												key:'short_name',
												type:'input',
												label:"Team short name",
											}],
										}]}
										intl={props.intl}
									/>

									<ButtonCollection
										in_floating={true}
									>
										<SaveButton
											onClick={afterTranslate}
										>Save</SaveButton>
									</ButtonCollection>
								</div>
							</FloatingLayer>
						)}
					</BuilderMainWithoutSidebar>
				</Route>
			</Switch>
		</Router>
	)
};

export default injectIntl(StageTeamsManagement);